import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, isNil } from 'lodash';
import { DATE_PRESET, END_DATE, START_DATE } from 'modules/campaigns/constants/queryParams';
import { REPORT_ID } from 'modules/campaigns/modules/campaigns-reports/hooks/useSelectedReportId';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { DIMENSION, useDimension } from '../../../../hooks';
import { isNetworkAccountType } from '../../../../modules/account-management';
import ACCOUNT_TYPE, { isDSPAccount, isNetworkSubAccount } from '../../../../modules/account-management/accountType';
import { FormattedMessage, LOCALE } from '../../../../modules/taboola-common-frontend-modules/i18n';
import { accountsMapSelector } from '../../../../selectors/baseSelectors';
import { CAMPAIGN_DIMENSION } from '../../../../services/campaignDimension';
import { AccountItemIcon } from '../../../AccountItemIcon/AccountItemIcon';
import { TOOLTIP_ALIGN } from '../../../Balance/Balance';
import { useBreadcrumbNavigation } from '../../hooks/useBreadcrumbNavigation';
import { usePickerTotal } from '../../hooks/usePickerTotal';
import { AccountBalance } from '../AccoutBalance/AccountBalance';
import { BreadcrumbsDropdown } from '../BreadcrumbsDropdown/BreadcrumbsDropdown';
import { BreadcrumbsSearchComponent } from '../BreadcrumbsDropdown/components/BreadcrumbsSearchComponent/BreadcrumbsSearchComponent';
import { DimensionPicker } from '../DimensionPicker/DimensionPicker';
import { DropdownOptionRenderer } from '../DropdownOptionRenderer/DropdownOptionRenderer';
import { fetchAccounts, fetchAccountsUnderNetwork } from './accountsFetchers';
import commonStyles from '../commonPickerDropdownStyle.module.scss';
import styles from './BreadcrumbsAccountPicker.module.scss';

export const PARAMS_TO_KEEP = [START_DATE, END_DATE, DATE_PRESET, DIMENSION, LOCALE, REPORT_ID];
const ACCOUNT_PICKER_TOTAL_KEY = 'ACCOUNT_PICKER_TOTAL_KEY';
const ACCOUNT_DATA_AUTOMATION_ID = 'account-picker';
const ROOT_ACCOUNT_DATA_AUTOMATION_ID = 'root-account-picker';
const OptionRenderer = ({ data, isRoot, ...rest }) => {
    return (
        <DropdownOptionRenderer
            data={data}
            icon={<AccountItemIcon {...data} hasOuterLink />}
            noIconPlaceholder={!isRoot}
            idPropName="id"
            namePropName="accountDescription"
            {...rest}
        />
    );
};

const getPreviewAccount = (selectedAccount, isRoot) => {
    if (isRoot) {
        if (isNetworkSubAccount(selectedAccount)) {
            return {
                id: selectedAccount.parentNetworkAccountId,
                accountName: selectedAccount.parentNetworkAccountName,
                accountDescription: selectedAccount.parentNetworkAccountDescription,
                type: ACCOUNT_TYPE.NETWORK,
            };
        }
        return selectedAccount;
    }

    if (isNetworkSubAccount(selectedAccount)) {
        return selectedAccount;
    }
};

export const BreadcrumbsAccountPicker = ({ isRoot }) => {
    const selectedAccount = useAccount();
    const previewAccount = getPreviewAccount(selectedAccount, isRoot);
    const accountsMap = useSelector(accountsMapSelector);
    const showAccountIcon = isDSPAccount(previewAccount);
    const [dimension] = useDimension();
    const dispatch = useDispatch();

    const fetcher = useCallback(
        ({ searchText, pageSize, page, queryKey }) => {
            if (isRoot) {
                return fetchAccounts(searchText, pageSize, page, queryKey, dispatch);
            } else {
                const networkAccountName = isNetworkAccountType(selectedAccount)
                    ? selectedAccount.accountName
                    : selectedAccount.parentNetworkAccountName;

                return fetchAccountsUnderNetwork(
                    searchText,
                    pageSize,
                    page,
                    networkAccountName,
                    dimension || CAMPAIGN_DIMENSION.SPONSORED,
                    `${queryKey}-${networkAccountName}`,
                    selectedAccount,
                    dispatch
                );
            }
        },
        [isRoot, selectedAccount, dimension, dispatch]
    );

    const dimensionTotalTrigger = isRoot ? null : dimension;
    const totalCount = usePickerTotal({
        fetcher,
        enabled: !isRoot && previewAccount?.id !== selectedAccount.id,
        queryKey: [ACCOUNT_PICKER_TOTAL_KEY, selectedAccount.id, dimensionTotalTrigger],
    });
    const allOptionsValue = isRoot
        ? null
        : {
              id: isNetworkAccountType(selectedAccount) ? selectedAccount?.id : selectedAccount.parentNetworkAccountId,
              accountDescription: <FormattedMessage id="app.breadcrumbs.all.accounts.option" />,
              hideId: true,
          };
    const accountValue = isNil(previewAccount?.id) ? (
        <FormattedMessage
            id="app.breadcrumbs.all.accounts.title"
            values={{ count: isNil(totalCount) ? '' : `(${totalCount})` }}
        />
    ) : (
        <span className={styles['description']}>
            {showAccountIcon && <AccountItemIcon {...selectedAccount} className={styles['account-icon-wrapper']} />}
            <span>
                {previewAccount.accountDescription ??
                    get(accountsMap, `${previewAccount?.id}.accountDescription`) ??
                    ''}
            </span>
        </span>
    );
    const OptionRendererCustomIcon = useCallback(props => <OptionRenderer {...props} isRoot={isRoot} />, [isRoot]);

    const handleSelectedOption = useBreadcrumbNavigation({
        paramsToKeep: PARAMS_TO_KEEP,
        selectedItem: selectedAccount,
    });
    return (
        <BreadcrumbsDropdown
            label={
                <>
                    <FormattedMessage
                        id={
                            isNetworkAccountType(previewAccount?.type)
                                ? 'app.breadcrumbs.account.network.title'
                                : 'app.breadcrumbs.account.title'
                        }
                        className={commonStyles['label']}
                    />
                    {previewAccount?.id === selectedAccount.id ? (
                        <AccountBalance tooltipAlign={isRoot ? TOOLTIP_ALIGN.ROOT : TOOLTIP_ALIGN.DEFAULT} />
                    ) : null}
                </>
            }
            value={accountValue}
            allOption={allOptionsValue}
            selectedItem={previewAccount}
            fetcher={fetcher}
            onSelect={handleSelectedOption}
            ItemComponent={OptionRendererCustomIcon}
            SearchComponent={BreadcrumbsSearchComponent}
            dataAutomationId={
                isNetworkAccountType(previewAccount?.type)
                    ? ROOT_ACCOUNT_DATA_AUTOMATION_ID
                    : ACCOUNT_DATA_AUTOMATION_ID
            }
            queryKey={[
                ACCOUNT_DATA_AUTOMATION_ID,
                isRoot ? 'root-account' : selectedAccount?.id,
                dimensionTotalTrigger,
            ]}
        >
            {!isRoot && <DimensionPicker />}
        </BreadcrumbsDropdown>
    );
};
